import { ApiError, fetchJson } from "./api";

export type ResearchAlertCadence = "daily" | "weekly" | "monthly";
export type ResearchAlertRetrievalMode = "hybrid" | "keyword" | "vector";
export type ResearchAlertRunStatus =
  | "pending"
  | "notified"
  | "skipped_empty"
  | "failed"
  | "unknown";

export type InboxDeliveryState = "created" | null;
export type EmailDeliveryState =
  | "pending"
  | "sending"
  | "sent"
  | "failed"
  | "suppressed"
  | "unknown"
  | null;

export interface ResearchAlertDelivery {
  inbox: InboxDeliveryState;
  email: EmailDeliveryState;
}

export interface ResearchAlertRun {
  digest_run_id: string;
  saved_search_id: string;
  saved_search_name: string;
  status: ResearchAlertRunStatus;
  executed_at: string | null;
  new_result_count: number | null;
  window_from: string | null;
  window_to: string | null;
  created_at: string;
  delivery: ResearchAlertDelivery;
  failure_code: string | null;
}

export interface ResearchAlert {
  saved_search_id: string;
  name: string;
  query: string;
  ticker: string | null;
  source: string | null;
  doc_type: string | null;
  date_from: string | null;
  date_to: string | null;
  retrieval_mode: ResearchAlertRetrievalMode;
  digest_enabled: boolean;
  digest_cadence: ResearchAlertCadence | null;
  created_at: string;
  updated_at: string;
  digest_last_checked_at: string | null;
  digest_last_notified_at: string | null;
  next_eligible_at: string | null;
  latest_run: ResearchAlertRun | null;
}

export interface ResearchAlertChannels {
  inbox: "available";
  email: "queued" | "suppressed";
}

export interface ResearchAlertsResponse {
  status: "ok";
  saved_searches: ResearchAlert[];
  count: number;
  runtime_available: boolean;
  channels: ResearchAlertChannels;
}

export interface ResearchAlertResponse {
  status: "ok";
  saved_search: ResearchAlert;
  runtime_available: boolean;
}

export interface ResearchAlertCreateRequest {
  name: string;
  query: string;
  ticker: string | null;
  source: string | null;
  doc_type: string | null;
  filters: { retrieval_mode: ResearchAlertRetrievalMode };
  digest_enabled: boolean;
  digest_cadence: ResearchAlertCadence;
}

export type ResearchAlertUpdateRequest = Partial<ResearchAlertCreateRequest>;

export interface ResearchAlertHistoryResponse {
  status: "ok";
  runs: ResearchAlertRun[];
  count: number;
  total_count: number;
  next_cursor: string | null;
}

export interface ResearchAlertPreviewItem {
  document_id: string;
  title: string;
  source_name: string | null;
  ticker: string | null;
  document_date: string | null;
  page: number | null;
  deep_link: string;
  ingested_at: string | null;
}

export interface ResearchAlertPreviewResponse {
  status: "ok";
  saved_search_id: string;
  name: string;
  window: { from: string | null; to: string | null };
  cursor_active: boolean;
  would_send: boolean;
  result_count: number;
  results: ResearchAlertPreviewItem[];
  truncated: boolean;
}

const DEV_ALERT_ID = "5a4b3c2d-0000-4000-8000-000000000001";
const DEV_PAUSED_ALERT_ID = "5a4b3c2d-0000-4000-8000-000000000002";

const DEV_LATEST_RUN: ResearchAlertRun = {
  digest_run_id: "7c6d5e4f-0000-4000-8000-000000000001",
  saved_search_id: DEV_ALERT_ID,
  saved_search_name: "TTD retail-media pressure",
  status: "notified",
  executed_at: "2026-08-09T06:15:00+00:00",
  new_result_count: 2,
  window_from: "2026-08-08T05:00:00+00:00",
  window_to: "2026-08-09T05:00:00+00:00",
  created_at: "2026-08-09T06:15:00+00:00",
  delivery: { inbox: "created", email: "pending" },
  failure_code: null,
};

let devAlertSequence = 2;
let devResearchAlerts: ResearchAlert[] = [
  {
    saved_search_id: DEV_ALERT_ID,
    name: DEV_LATEST_RUN.saved_search_name,
    query: "Amazon DSP pricing pressure and retail-media budget shifts",
    ticker: "TTD",
    source: "citi",
    doc_type: "broker_note",
    date_from: null,
    date_to: null,
    retrieval_mode: "hybrid",
    digest_enabled: true,
    digest_cadence: "daily",
    created_at: "2026-07-10T08:00:00+00:00",
    updated_at: "2026-08-09T06:15:00+00:00",
    digest_last_checked_at: "2026-08-09T06:15:00+00:00",
    digest_last_notified_at: "2026-08-09T06:15:00+00:00",
    next_eligible_at: "2026-08-10T06:15:00+00:00",
    latest_run: DEV_LATEST_RUN,
  },
  {
    saved_search_id: DEV_PAUSED_ALERT_ID,
    name: "MU NAND pricing evidence",
    query: "NAND contract pricing inventory correction channel checks",
    ticker: "MU",
    source: null,
    doc_type: null,
    date_from: null,
    date_to: null,
    retrieval_mode: "keyword",
    digest_enabled: false,
    digest_cadence: "weekly",
    created_at: "2026-07-20T09:00:00+00:00",
    updated_at: "2026-08-08T07:00:00+00:00",
    digest_last_checked_at: "2026-08-08T07:00:00+00:00",
    digest_last_notified_at: null,
    next_eligible_at: null,
    latest_run: {
      digest_run_id: "7c6d5e4f-0000-4000-8000-000000000003",
      saved_search_id: DEV_PAUSED_ALERT_ID,
      saved_search_name: "MU NAND pricing evidence",
      status: "skipped_empty",
      executed_at: "2026-08-08T07:00:00+00:00",
      new_result_count: 0,
      window_from: "2026-08-01T06:00:00+00:00",
      window_to: "2026-08-08T06:00:00+00:00",
      created_at: "2026-08-08T07:00:00+00:00",
      delivery: { inbox: null, email: null },
      failure_code: null,
    },
  },
];

const devResearchAlertRuns = new Map<string, ResearchAlertRun[]>([
  [
    DEV_ALERT_ID,
    [
      DEV_LATEST_RUN,
      {
        ...DEV_LATEST_RUN,
        digest_run_id: "7c6d5e4f-0000-4000-8000-000000000002",
        status: "skipped_empty",
        executed_at: "2026-08-08T06:15:00+00:00",
        new_result_count: 0,
        window_from: "2026-08-07T05:00:00+00:00",
        window_to: "2026-08-08T05:00:00+00:00",
        created_at: "2026-08-08T06:15:00+00:00",
        delivery: { inbox: null, email: null },
      },
    ],
  ],
  [DEV_PAUSED_ALERT_ID, [devResearchAlerts[1].latest_run as ResearchAlertRun]],
]);

const DEV_PREVIEW_RESULTS: ResearchAlertPreviewItem[] = [
  {
    document_id: "6f9d2b1a-0000-4000-8000-000000000001",
    title: "Navigating Macro Headwinds — Ad Agency Survey",
    source_name: "Citi",
    ticker: "TTD",
    document_date: "2026-08-08",
    page: 8,
    deep_link: "/app/documents/6f9d2b1a-0000-4000-8000-000000000001?page=8",
    ingested_at: "2026-08-09T05:30:00+00:00",
  },
];

async function withResearchAlertsDevFallback<T>(
  real: () => Promise<T>,
  fallback: () => T | Promise<T>,
): Promise<T> {
  if (!import.meta.env.DEV) return real();
  try {
    return await real();
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") throw error;
    if (
      error instanceof ApiError &&
      ![404, 500, 502, 503, 504].includes(error.status)
    ) {
      throw error;
    }
    return fallback();
  }
}

function cloneAlert(alert: ResearchAlert): ResearchAlert {
  return {
    ...alert,
    latest_run: alert.latest_run
      ? { ...alert.latest_run, delivery: { ...alert.latest_run.delivery } }
      : null,
  };
}

function devAlertId(): string {
  devAlertSequence += 1;
  return `5a4b3c2d-0000-4000-8000-${String(devAlertSequence).padStart(12, "0")}`;
}

function requireDevAlert(savedSearchId: string): ResearchAlert {
  const alert = devResearchAlerts.find((item) => item.saved_search_id === savedSearchId);
  if (!alert) throw new Error("Research alert not found");
  return alert;
}

function devNextEligibleAt(
  enabled: boolean,
  cadence: ResearchAlertCadence | null,
  checkedAt: string | null,
  now: string,
): string | null {
  if (!enabled || cadence === null) return null;
  if (checkedAt === null) return now;
  const checked = new Date(checkedAt);
  if (!Number.isFinite(checked.getTime())) return now;
  if (cadence === "daily" || cadence === "weekly") {
    checked.setUTCDate(checked.getUTCDate() + (cadence === "weekly" ? 7 : 1));
    return checked.toISOString();
  }
  const day = checked.getUTCDate();
  checked.setUTCDate(1);
  checked.setUTCMonth(checked.getUTCMonth() + 1);
  const lastDay = new Date(
    Date.UTC(checked.getUTCFullYear(), checked.getUTCMonth() + 1, 0),
  ).getUTCDate();
  checked.setUTCDate(Math.min(day, lastDay));
  return checked.toISOString();
}

function jsonRequest<T>(path: string, method: "POST" | "PATCH", body: unknown): Promise<T> {
  return fetchJson<T>(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function alertPath(savedSearchId: string): string {
  return `/api/portal/saved-searches/${encodeURIComponent(savedSearchId)}`;
}

export function fetchResearchAlerts(): Promise<ResearchAlertsResponse> {
  return withResearchAlertsDevFallback(
    () => fetchJson<ResearchAlertsResponse>("/api/portal/saved-searches"),
    () => ({
      status: "ok",
      saved_searches: devResearchAlerts.map(cloneAlert),
      count: devResearchAlerts.length,
      runtime_available: true,
      channels: { inbox: "available", email: "queued" },
    }),
  );
}

export function createResearchAlert(
  request: ResearchAlertCreateRequest,
): Promise<ResearchAlertResponse> {
  return withResearchAlertsDevFallback(
    () => jsonRequest<ResearchAlertResponse>("/api/portal/saved-searches", "POST", request),
    () => {
      const now = new Date().toISOString();
      const savedSearchId = devAlertId();
      const alert: ResearchAlert = {
        saved_search_id: savedSearchId,
        name: request.name,
        query: request.query,
        ticker: request.ticker,
        source: request.source,
        doc_type: request.doc_type,
        date_from: null,
        date_to: null,
        retrieval_mode: request.filters.retrieval_mode,
        digest_enabled: request.digest_enabled,
        digest_cadence: request.digest_cadence,
        created_at: now,
        updated_at: now,
        digest_last_checked_at: null,
        digest_last_notified_at: null,
        next_eligible_at: devNextEligibleAt(
          request.digest_enabled,
          request.digest_cadence,
          null,
          now,
        ),
        latest_run: null,
      };
      devResearchAlerts = [alert, ...devResearchAlerts];
      devResearchAlertRuns.set(savedSearchId, []);
      return { status: "ok", saved_search: cloneAlert(alert), runtime_available: true };
    },
  );
}

export function updateResearchAlert(
  savedSearchId: string,
  request: ResearchAlertUpdateRequest,
): Promise<ResearchAlertResponse> {
  return withResearchAlertsDevFallback(
    () => jsonRequest<ResearchAlertResponse>(alertPath(savedSearchId), "PATCH", request),
    () => {
      const current = requireDevAlert(savedSearchId);
      const now = new Date().toISOString();
      const digestEnabled = request.digest_enabled ?? current.digest_enabled;
      const digestCadence = request.digest_cadence ?? current.digest_cadence;
      const updated: ResearchAlert = {
        ...current,
        name: request.name ?? current.name,
        query: request.query ?? current.query,
        ticker: request.ticker !== undefined ? request.ticker : current.ticker,
        source: request.source !== undefined ? request.source : current.source,
        doc_type: request.doc_type !== undefined ? request.doc_type : current.doc_type,
        retrieval_mode: request.filters?.retrieval_mode ?? current.retrieval_mode,
        digest_enabled: digestEnabled,
        digest_cadence: digestCadence,
        updated_at: now,
        next_eligible_at: devNextEligibleAt(
          digestEnabled,
          digestCadence,
          current.digest_last_checked_at,
          now,
        ),
      };
      devResearchAlerts = devResearchAlerts.map((item) =>
        item.saved_search_id === savedSearchId ? updated : item,
      );
      return { status: "ok", saved_search: cloneAlert(updated), runtime_available: true };
    },
  );
}

export function deleteResearchAlert(
  savedSearchId: string,
): Promise<{ status: "ok"; deleted: string }> {
  return withResearchAlertsDevFallback(
    () =>
      fetchJson<{ status: "ok"; deleted: string }>(alertPath(savedSearchId), {
        method: "DELETE",
      }),
    () => {
      requireDevAlert(savedSearchId);
      devResearchAlerts = devResearchAlerts.filter(
        (item) => item.saved_search_id !== savedSearchId,
      );
      devResearchAlertRuns.delete(savedSearchId);
      return { status: "ok", deleted: savedSearchId };
    },
  );
}

export function fetchResearchAlertHistory(
  savedSearchId: string,
  cursor: string | null,
  limit = 8,
): Promise<ResearchAlertHistoryResponse> {
  const params = new URLSearchParams({
    saved_search_id: savedSearchId,
    limit: String(limit),
  });
  if (cursor) params.set("cursor", cursor);
  return withResearchAlertsDevFallback(
    () =>
      fetchJson<ResearchAlertHistoryResponse>(`/api/portal/digests?${params.toString()}`),
    () => {
      requireDevAlert(savedSearchId);
      const runs = devResearchAlertRuns.get(savedSearchId) ?? [];
      const offset = cursor?.startsWith("dev:") ? Number(cursor.slice(4)) : 0;
      const safeOffset = Number.isSafeInteger(offset) && offset >= 0 ? offset : 0;
      const page = runs.slice(safeOffset, safeOffset + limit).map((run) => ({
        ...run,
        delivery: { ...run.delivery },
      }));
      const nextOffset = safeOffset + page.length;
      return {
        status: "ok",
        runs: page,
        count: page.length,
        total_count: runs.length,
        next_cursor: nextOffset < runs.length ? `dev:${nextOffset}` : null,
      };
    },
  );
}

export function previewResearchAlert(
  savedSearchId: string,
): Promise<ResearchAlertPreviewResponse> {
  return withResearchAlertsDevFallback(
    () =>
      jsonRequest<ResearchAlertPreviewResponse>(
        `${alertPath(savedSearchId)}/digest/preview`,
        "POST",
        {},
      ),
    () => {
      const alert = requireDevAlert(savedSearchId);
      const results = alert.ticker === "TTD" ? DEV_PREVIEW_RESULTS : [];
      return {
        status: "ok",
        saved_search_id: savedSearchId,
        name: alert.name,
        window: {
          from: alert.digest_last_checked_at,
          to: "2026-08-09T11:00:00+00:00",
        },
        cursor_active: alert.digest_last_checked_at !== null,
        would_send: results.length > 0,
        result_count: results.length,
        results: results.map((item) => ({ ...item })),
        truncated: false,
      };
    },
  );
}
